import React, { memo, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ReplacementLink } from '../../../supplierReturn/components/ReplacementLink';
import styles from './styles.module.css';
import {
  Button,
  Input,
  Dropdown,
  SearchableSelect,
  InputWithSelect,
} from '../../../../../common/ui';
import { showAlert } from '../../../../../common/actions';
import { getSuppliers } from '../../../suppliers/actions';
import Axios from '../../../../../common/util/Axios';
import {
  searchProductsForAddStock,
  getProductByIdForAddStock,
  createStock,
} from '../../actions';

const QTY_UNITS = [
  { id: 'pcs', name: 'pcs' },
  { id: 'kg', name: 'kg' },
  { id: 'g', name: 'g' },
  { id: 'l', name: 'l' },
  { id: 'ml', name: 'ml' },
  { id: 'm', name: 'm' },
  { id: 'cm', name: 'cm' },
];

const DISCOUNT_TYPES = [
  { id: 'TOTAL_AMOUNT', name: 'Total Amount' },
  { id: 'PER_UNIT', name: 'Per Unit' },
];

const DISCOUNT_UNITS = [
  { id: 'Rs', name: 'Rs' },
  { id: '%', name: '%' },
];

const PROFIT_MARGIN_TYPE_OPTIONS = [
  { value: 'selling', label: 'Selling price based (Recommended)' },
  { value: 'cost', label: 'Cost price based' },
];

const NUMERIC_REGEX = /^[0-9]*\.?[0-9]*$/;

const initialStock = {
  productId: null,
  supplierId: null,
  unitCost: '',
  qty: '',
  unit: 'pcs',
  discountType: '',
  discount: { unit: 'Rs', value: '' },
  costOfGoods: '',
  sellingPrice: '',
  sellingDiscount: { unit: 'Rs', value: '' },
  sellingLoyaltyDiscount: { unit: 'Rs', value: '' },
  sellingWholesaleDiscount: { unit: 'Rs', value: '' },
};

const initialWarrantyDetails = {
  serialNumber: '',
  serialRange: { min: '', max: '' },
  serialNumberList: [],
  warranty: [],
  warrantyType: null,
  warrantyDescription: '',
};

function getUnixTimestampFromDate(d) {
  return Math.floor((d instanceof Date ? d : new Date(d)).getTime() / 1000);
}

function getUnixTimestampFromDateString(dateStr) {
  if (!dateStr || !dateStr.trim()) return null;
  const normalized = String(dateStr).replace(/\//g, '-').trim();
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : Math.floor(date.getTime() / 1000);
}

/** Label for product search: code – name – variantName (API may send baseName + variantName). */
function getProductSearchOptionLabel(o) {
  if (!o || typeof o !== 'object') return '—';
  const mainName =
    o.baseName != null && String(o.baseName).trim() !== '' ? o.baseName : o.name;
  const parts = [o.code, mainName, o.variantName].filter(
    (x) => x != null && String(x).trim() !== ''
  );
  return parts.join(' – ') || '—';
}

const AddNewStock = memo(
  ({
    stocksState: { suggestProducts, isSuggestedProductsLoading },
    auth: { companyId, branchId },
    suppliersState: { suppliersData },
    getSuppliers,
    searchProductsForAddStock,
    getProductByIdForAddStock,
    createStock,
    showAlert,
  }) => {
    const [searchParams] = useSearchParams();
    const productIdFromQuery = searchParams.get('productId');
    const redirectPathFromQuery = searchParams.get('redirectPath');
    const navigate = useNavigate();
    const location = useLocation();
    const [relatedSupplierReturnId, setRelatedSupplierReturnId] = useState('');
    const [replacementReturnNo, setReplacementReturnNo] = useState('');

    const [newStock, setNewStock] = useState(initialStock);

    useEffect(() => {
      const state = location.state || {};
      if (state.relatedSupplierReturnId) {
        setRelatedSupplierReturnId(String(state.relatedSupplierReturnId));
        setReplacementReturnNo(state.returnNo || '');
      } else {
        setRelatedSupplierReturnId('');
        setReplacementReturnNo('');
      }
      if (state.supplierId) {
        setNewStock((prev) => (prev.supplierId ? prev : { ...prev, supplierId: state.supplierId }));
      }
    }, [location.state]);
    const [profitMargin, setProfitMargin] = useState('');
    const [profitMarginUnit, setProfitMarginUnit] = useState('%');
    const [profitMargingType, setProfitMargingType] = useState('selling');
    const [finalSellingPrice, setFinalSellingPrice] = useState('0');
    const [expDate, setExpDate] = useState('');
    const [selectedProductName, setSelectedProductName] = useState('');
    const [productOptions, setProductOptions] = useState([]);
    const [isUnitCostDisabled, setUnitCostDisabled] = useState(false);
    const [isCostOfGoodsDisabled, setCostOfGoodsDisabled] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const [errors, setErrors] = useState({});
    const [warrantyTypeOptions, setWarrantyTypeOptions] = useState([]);
    const [displayWarrantyPeriod, setDisplayWarrantyPeriod] = useState('');
    const [warrantyDetails, setWarrantyDetails] = useState(initialWarrantyDetails);
    const [serialInputMethod, setSerialInputMethod] = useState(null); // null | 'textField' | 'serialRange'
    const [selectedProductObj, setSelectedProductObj] = useState(null);

    const supplierOptions = Array.isArray(suppliersData) ? suppliersData : [];
    const productSearchOptions = Array.isArray(suggestProducts) ? suggestProducts : [];

    const isSerialRangeDisabled = serialInputMethod === 'textField';
    const isSingleSerialDisabled = serialInputMethod === 'serialRange';

    const uiWarrantyTypeOptions = useMemo(
      () =>
        warrantyTypeOptions.map((w) => ({
          value: w.id,
          label: w.name ?? String(w.id),
          ...w,
        })),
      [warrantyTypeOptions]
    );

    const getSerialValue = (item) =>
      typeof item === 'object' && item !== null && item.serialNumber != null ? item.serialNumber : String(item);

    const addSerialNumber = () => {
      const s = String(warrantyDetails.serialNumber || '').trim();
      if (!s) return;
      if (!newStock.qty || parseFloat(newStock.qty) <= 0) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please enter quantity before adding serial numbers' });
        return;
      }
      const currentSerialCount = warrantyDetails.serialNumberList.length;
      const maxQty = parseFloat(newStock.qty);
      if (currentSerialCount >= maxQty) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: `Cannot add more serial numbers. Maximum allowed: ${maxQty}` });
        return;
      }
      const alreadyExists = warrantyDetails.serialNumberList.some((item) => getSerialValue(item) === s);
      if (alreadyExists) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Serial number already exists.' });
        return;
      }
      setSerialInputMethod('textField');
      setWarrantyDetails((prev) => ({
        ...prev,
        serialNumberList: [...prev.serialNumberList, s],
        serialNumber: '',
      }));
    };

    const removeSerialAt = (index) => {
      setWarrantyDetails((prev) => {
        const newList = prev.serialNumberList.filter((_, i) => i !== index);
        if (newList.length === 0) setSerialInputMethod(null);
        return { ...prev, serialNumberList: newList };
      });
    };

    const clearAllSerials = () => {
      setWarrantyDetails((prev) => ({ ...prev, serialNumberList: [] }));
      setSerialInputMethod(null);
    };

    const handleGenerateSerialRange = () => {
      const min = String(warrantyDetails.serialRange.min || '').trim();
      const max = String(warrantyDetails.serialRange.max || '').trim();
      if (!min || !max) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please enter both min and max serial numbers' });
        return;
      }
      if (!newStock.qty || parseFloat(newStock.qty) <= 0) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please enter quantity before adding serial numbers' });
        return;
      }
      const isNumericRegex = /^\d+$/;
      if (!isNumericRegex.test(min) || !isNumericRegex.test(max)) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Serial range must contain only numeric values' });
        return;
      }
      const minNum = parseInt(min, 10);
      const maxNum = parseInt(max, 10);
      if (maxNum <= minNum) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Max serial number must be greater than min' });
        return;
      }
      const rangeSize = maxNum - minNum + 1;
      if (rangeSize > 1000) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Serial range is too large (max 1000 items)' });
        return;
      }
      const currentSerialCount = warrantyDetails.serialNumberList.length;
      const maxQty = parseFloat(newStock.qty);
      if (currentSerialCount + rangeSize > maxQty) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: `Cannot add ${rangeSize} serial numbers. Current: ${currentSerialCount}, Maximum allowed: ${maxQty}`,
        });
        return;
      }
      const generatedSerials = [];
      let skippedCount = 0;
      for (let i = minNum; i <= maxNum; i++) {
        const serialNumberStr = i.toString();
        const alreadyExists = warrantyDetails.serialNumberList.some((item) => getSerialValue(item) === serialNumberStr);
        if (!alreadyExists) {
          generatedSerials.push(serialNumberStr);
        } else {
          skippedCount++;
        }
      }
      if (skippedCount > 0) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: `${skippedCount} serial number(s) were skipped because they already exist.` });
      }
      setSerialInputMethod('serialRange');
      setWarrantyDetails((prev) => ({
        ...prev,
        serialNumberList: [...prev.serialNumberList, ...generatedSerials],
        serialRange: { min: '', max: '' },
      }));
    };

    const addWarranty = () => {
      if (!warrantyDetails.warrantyType) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please select a warranty type' });
        return;
      }
      const warrantyObject = {
        warrantyType: warrantyDetails.warrantyType,
        description: warrantyDetails.warrantyDescription || '',
      };
      setWarrantyDetails((prev) => ({
        ...prev,
        warranty: [...prev.warranty, warrantyObject],
        warrantyType: null,
        warrantyDescription: '',
      }));
      setDisplayWarrantyPeriod('');
    };

    const removeWarrantyAt = (index) => {
      setWarrantyDetails((prev) => ({
        ...prev,
        warranty: prev.warranty.filter((_, i) => i !== index),
      }));
    };

    const clearAllWarranties = () => {
      setWarrantyDetails((prev) => ({ ...prev, warranty: [] }));
      setDisplayWarrantyPeriod('');
    };

    useEffect(() => {
      getSuppliers();
    }, [getSuppliers]);

    useEffect(() => {
      if (!companyId) return;
      Axios.get('/api/dashboard/settings/find-warranty-periods', { params: { companyId } })
        .then((res) => {
          const data = res.data?.data ?? res.data;
          setWarrantyTypeOptions(Array.isArray(data) ? data : []);
        })
        .catch(() => setWarrantyTypeOptions([]));
    }, [companyId]);

    useEffect(() => {
      if (!productIdFromQuery) return;
      let cancelled = false;
      getProductByIdForAddStock(productIdFromQuery)().then((product) => {
        if (cancelled || !product) return;
        setNewStock((prev) => ({ ...prev, productId: product.id }));
        setSelectedProductName(product.name || '');
        setProductOptions([product]);
        setSelectedProductObj(product);
      });
      return () => { cancelled = true; };
    }, [productIdFromQuery, getProductByIdForAddStock]);

    useEffect(() => {
      const price = parseFloat(newStock.sellingPrice);
      const discountVal = parseFloat(newStock.sellingDiscount.value);
      const numericPrice = Number.isFinite(price) ? price : 0;
      const numericDiscount = Number.isFinite(discountVal) ? discountVal : 0;
      let final = numericPrice;
      if (newStock.sellingDiscount.unit === 'Rs') {
        final = numericPrice - numericDiscount;
      } else {
        final = numericPrice - (numericPrice * numericDiscount) / 100;
      }
      setFinalSellingPrice(Number.isFinite(final) ? final.toFixed(2) : '0');
    }, [newStock.sellingPrice, newStock.sellingDiscount.value, newStock.sellingDiscount.unit]);

    const onchangeBuyingValue = useCallback(
      ({
        unitCost = newStock.unitCost || 0,
        quantity = newStock.qty || 0,
        discountType = newStock.discountType,
        discountValue = newStock.discount.value || 0,
        discountUnit = newStock.discount.unit,
        costOfGoods = newStock.costOfGoods || 0,
        isCostOfGoodsEntered = isUnitCostDisabled,
        isUnitCostEntered = isCostOfGoodsDisabled,
      }) => {
        let totalDiscountAmount = 0;
        if (discountType === 'TOTAL_AMOUNT' && discountUnit === 'Rs') {
          totalDiscountAmount = Number(discountValue) || 0;
        } else if (discountType === 'PER_UNIT' && discountUnit === 'Rs') {
          totalDiscountAmount = (Number(discountValue) || 0) * quantity;
        } else if (discountUnit === '%') {
          const dv = Number(discountValue) || 0;
          const cg = Number(costOfGoods) || 0;
          const uc = Number(unitCost) || 0;
          if (isCostOfGoodsEntered && cg > 0) {
            totalDiscountAmount = (cg / (100 - dv)) * dv;
          } else if (isUnitCostEntered) {
            totalDiscountAmount = (uc * quantity * dv) / 100;
          }
          totalDiscountAmount = Math.round(totalDiscountAmount * 100) / 100;
        }
        let calculatedUnitPrice;
        let calculatedTotalCost;
        const ucNum = Number(unitCost) || 0;
        if (isCostOfGoodsEntered && quantity > 0) {
          calculatedUnitPrice = ((Number(costOfGoods) || 0) + totalDiscountAmount) / quantity;
          calculatedUnitPrice = calculatedUnitPrice.toFixed(2);
        } else if (isUnitCostEntered) {
          calculatedTotalCost = (ucNum * quantity - totalDiscountAmount).toFixed(2);
        }
        return {
          calculatedUnitPrice: calculatedUnitPrice != null ? calculatedUnitPrice : newStock.unitCost,
          calculatedTotalCost: calculatedTotalCost != null ? calculatedTotalCost : newStock.costOfGoods,
        };
      },
      [newStock, isUnitCostDisabled, isCostOfGoodsDisabled]
    );

    const calculateSellingPrice = useCallback(
      ({ unitCost = newStock.unitCost, profitMargin: pm = profitMargin, profitMarginUnit: pu = profitMarginUnit, profitMargingType: pt = profitMargingType }) => {
        const uc = Number(unitCost) || 0;
        const margin = Number(pm) || 0;
        if (pu === '%') {
          if (pt === 'selling') return (uc / (1 - margin / 100)).toFixed(2);
          return (uc * (1 + margin / 100)).toFixed(2);
        }
        return (uc + margin).toFixed(2);
      },
      [newStock.unitCost, profitMargin, profitMarginUnit, profitMargingType]
    );

    const calculateProfitMargin = useCallback(
      ({ unitCost = newStock.unitCost, sellingPrice = newStock.sellingPrice, profitMarginUnit: pu = profitMarginUnit }) => {
        const uc = Number(unitCost) || 0;
        const sp = Number(sellingPrice) || 0;
        if (pu === '%') {
          if (profitMargingType === 'selling' && sp > 0) return (((sp - uc) / sp) * 100).toFixed(2);
          if (uc > 0) return (((sp - uc) / uc) * 100).toFixed(2);
        }
        return (sp - uc).toFixed(2);
      },
      [newStock.unitCost, newStock.sellingPrice, profitMarginUnit, profitMargingType]
    );

    const handleProductSearch = (keyword) => {
      if (keyword != null && String(keyword).trim().length >= 1) {
        searchProductsForAddStock(String(keyword).trim());
      } else {
        setProductOptions([]);
      }
    };

    const handleSelectProduct = (option) => {
      if (!option) {
        setNewStock((prev) => ({ ...prev, productId: null }));
        setSelectedProductName('');
        setSelectedProductObj(null);
        return;
      }
      setNewStock((prev) => ({ ...prev, productId: option.id }));
      setSelectedProductName(getProductSearchOptionLabel(option));
      setSelectedProductObj(option);
    };

    const optionsForProduct = productIdFromQuery && productOptions.length > 0 ? productOptions : productSearchOptions;

    const validate = () => {
      const e = {};
      if (!newStock.productId) e.product = 'Product is required.';
      if (newStock.supplierId == null || newStock.supplierId === '') e.supplier = 'Supplier is required.';
      if (!newStock.unitCost) e.unitCost = 'Unit cost is required.';
      if (!newStock.qty) e.quantity = 'Quantity is required.';
      if (newStock.qty && !newStock.unit) e.unit = 'Unit is required.';
      if (newStock.discountType && !newStock.discount.value) e.discount = 'Discount value is required.';
      if (newStock.discount.value && !newStock.discountType) e.discountType = 'Discount type is required.';
      if (!newStock.costOfGoods) e.costOfGoods = 'Cost of goods is required.';
      if (!newStock.sellingPrice) e.sellingPrice = 'Selling price is required.';
      if (selectedProductObj?.isSerialCheck && warrantyDetails.serialNumberList.length === 0) {
        showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please add serial numbers for this product.' });
        return false;
      }
      if (warrantyDetails.serialNumberList.length > 0) {
        if (warrantyDetails.serialNumberList.length < parseFloat(newStock.qty)) {
          showAlert({
            isAlertOpen: true,
            alertType: 'danger',
            alertMessage: `Please add ${parseFloat(newStock.qty) - warrantyDetails.serialNumberList.length} more serial numbers. You have ${newStock.qty} items in stock.`,
          });
          return false;
        }
      }
      setErrors(e);
      return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (submittingRef.current) return;
      submittingRef.current = true;
      setIsSubmitting(true);
      try {
      if (!validate()) return;
      const createdAt = new Date().toISOString();
      const formattedExpDate = expDate ? getUnixTimestampFromDateString(expDate.replace(/\//g, '-')) : null;

      const costOfGoodsNum = Number(newStock.costOfGoods) || 0;
      const unitCostNum = Number(newStock.unitCost) || 0;
      const qtyNum = Number(newStock.qty) || 0;
      const sellingPriceNum = Number(newStock.sellingPrice) || 0;
      const discountVal = Number(newStock.sellingDiscount.value) || 0;
      const wholesaleDiscount =
        newStock.sellingWholesaleDiscount.unit === 'Rs'
          ? Number(newStock.sellingWholesaleDiscount.value) || 0
          : (sellingPriceNum * (Number(newStock.sellingWholesaleDiscount.value) || 0)) / 100;

      const stock = {
        created_at: createdAt,
        expire_at: formattedExpDate,
        initial_qty: qtyNum,
        qty: qtyNum,
        unit: newStock.unit || 'pcs',
        cost_of_goods: costOfGoodsNum,
        unit_cost: unitCostNum,
        selling_price: sellingPriceNum,
        product_id: newStock.productId,
        discount: discountVal,
        discount_unit: newStock.sellingDiscount.unit || 'Rs',
        supplier_id: newStock.supplierId,
        wholesale_discount: wholesaleDiscount,
        isTemporary: null,
        companyId,
        branchId,
      };

      if (relatedSupplierReturnId) {
        stock.relatedSupplierReturnId = relatedSupplierReturnId;
      }

      const requestBody = { stock };

      if (newStock.sellingLoyaltyDiscount.value && newStock.sellingLoyaltyDiscount.unit) {
        requestBody.promotion = {
          productid: newStock.productId,
          value: Number(newStock.sellingLoyaltyDiscount.value) || 0,
          unit: newStock.sellingLoyaltyDiscount.unit,
          productname: selectedProductName,
          createdat: createdAt,
          startfrom: createdAt,
          expiredat: null,
          is_loyalty_promo: 1,
          stock_id: null,
          promotion_type: 'PRODUCT_DISCOUNT',
          rule: null,
          rule_variables: null,
          companyId,
          branchId,
        };
      }

      if (warrantyDetails.serialNumberList.length > 0 || warrantyDetails.warranty.length > 0) {
        requestBody.warranty = {
          serialNumberList: warrantyDetails.serialNumberList,
          warranty: warrantyDetails.warranty,
        };
      }

      const success = await createStock(requestBody);
      if (success === true) {
        showAlert({ isAlertOpen: true, alertType: 'success', alertMessage: 'New Stock created successfully.' });
        if (redirectPathFromQuery) navigate(redirectPathFromQuery);
        else handleReset();
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: typeof success === 'string' && success.trim()
            ? success
            : 'Something went wrong. Please try again.',
        });
      }
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    };

    const handleReset = () => {
      setNewStock(initialStock);
      setWarrantyDetails(initialWarrantyDetails);
      setExpDate('');
      setSelectedProductName('');
      setSelectedProductObj(null);
      setProductOptions(productIdFromQuery ? productOptions : []);
      setProfitMargin('');
      setProfitMarginUnit('%');
      setProfitMargingType('selling');
      setUnitCostDisabled(false);
      setCostOfGoodsDisabled(false);
      setErrors({});
      setSerialInputMethod(null);
      setDisplayWarrantyPeriod('');
    };

    const handleCancel = () => {
      if (redirectPathFromQuery) navigate(redirectPathFromQuery);
      else navigate('/inventory/stocks');
    };

    const setStock = (updater) => setNewStock((prev) => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));

    const unitOptions = QTY_UNITS.map((u) => ({ value: u.name, label: u.name }));
    const discountTypeOptions = DISCOUNT_TYPES.map((d) => ({ value: d.id, label: d.name }));
    const discountUnitOptions = DISCOUNT_UNITS.map((u) => ({ value: u.name, label: u.name }));

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Add New Stock</h1>
            <p className={styles.breadcrumb}>Inventory / Stocks / Add New Stock</p>
          </div>
        </div>
        <div className={styles.body}>
          <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
            <ReplacementLink
              supplierId={newStock.supplierId}
              value={relatedSupplierReturnId}
              returnNo={replacementReturnNo}
              onChange={(nextId, nextNo) => {
                setRelatedSupplierReturnId(nextId);
                setReplacementReturnNo(nextNo);
              }}
            />
            <div className={styles.headerBlock}>
              <div className={styles.productRow}>
                <div className={styles.productSelectWrap}>
                  <SearchableSelect
                    async
                    label="Select Product by Name, Code, or Barcode"
                    options={optionsForProduct}
                    getOptionLabel={getProductSearchOptionLabel}
                    getOptionValue={(o) => o.id}
                    value={newStock.productId}
                    onChange={(id) => {
                      setStock({ productId: id || null });
                      if (!id) setSelectedProductName('');
                    }}
                    onSelect={handleSelectProduct}
                    onSearch={handleProductSearch}
                    placeholder="Search by name, code or barcode"
                    minSearchLength={1}
                    searchDebounceMs={300}
                    isLoading={isSuggestedProductsLoading}
                    error={errors.product}
                  />
                  {errors.product && <span className={styles.fieldError}>{errors.product}</span>}
                </div>
              </div>
              <div className={styles.supplierWrap}>
                <SearchableSelect
                  label="Supplier"
                  options={supplierOptions}
                  getOptionLabel={(o) => o.companyname || o.name || ''}
                  getOptionValue={(o) => o.id}
                  value={newStock.supplierId}
                  onChange={(id) => {
                    if (String(newStock.supplierId || '') !== String(id || '')) {
                      setRelatedSupplierReturnId('');
                      setReplacementReturnNo('');
                    }
                    setStock({ supplierId: id });
                  }}
                  placeholder="Not selected"
                  error={errors.supplier}
                />
                {errors.supplier && <span className={styles.fieldError}>{errors.supplier}</span>}
              </div>
            </div>

            <div className={styles.divider} />

            <section className={styles.formSection}>
              <h2 className={styles.sectionTitle}>Buying Section</h2>
              <p className={styles.sectionCaption}>Please add your buying information</p>
              <div className={styles.fieldGrid}>
                <div className={styles.fieldGroup}>
                  <Input
                    label="Unit Cost"
                    type="text"
                    value={newStock.unitCost}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setErrors((e) => ({ ...e, unitCost: '' }));
                      if (v === '') {
                        setCostOfGoodsDisabled(false);
                        setStock({ unitCost: '', costOfGoods: '' });
                        return;
                      }
                      setStock({ unitCost: v });
                      const num = parseFloat(v);
                      if (!Number.isNaN(num)) {
                        setCostOfGoodsDisabled(true);
                        const { calculatedTotalCost } = onchangeBuyingValue({
                          unitCost: num,
                          isCostOfGoodsEntered: false,
                          isUnitCostEntered: true,
                        });
                        setStock((prev) => ({ ...prev, costOfGoods: calculatedTotalCost }));
                      }
                    }}
                    disabled={isUnitCostDisabled}
                    error={errors.unitCost}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <InputWithSelect
                    label="Quantity"
                    value={newStock.qty}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setErrors((e) => ({ ...e, quantity: '' }));
                      setStock({ qty: v });
                      const num = parseFloat(v);
                      if (!Number.isNaN(num)) {
                        const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ quantity: num });
                        setStock((prev) => ({ ...prev, unitCost: calculatedUnitPrice, costOfGoods: calculatedTotalCost }));
                      }
                    }}
                    selectOptions={unitOptions}
                    selectValue={newStock.unit}
                    onSelectChange={(val) => setStock({ unit: val || 'pcs' })}
                    selectPlaceholder="Unit"
                    error={errors.quantity || errors.unit}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <Dropdown
                    label="Discount Type"
                    options={discountTypeOptions}
                    value={newStock.discountType}
                    onChange={(val) => {
                      setErrors((e) => ({ ...e, discountType: '' }));
                      const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ discountType: val });
                      setStock({
                        discountType: val,
                        unitCost: calculatedUnitPrice,
                        costOfGoods: calculatedTotalCost,
                      });
                    }}
                    placeholder="Not selected"
                    error={errors.discountType}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <InputWithSelect
                    label="Discount value"
                    value={newStock.discount.value}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setErrors((e) => ({ ...e, discount: '' }));
                      setStock((prev) => ({ ...prev, discount: { ...prev.discount, value: v } }));
                      const num = parseFloat(v);
                      if (!Number.isNaN(num)) {
                        const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ discountValue: num });
                        setStock((prev) => ({ ...prev, unitCost: calculatedUnitPrice, costOfGoods: calculatedTotalCost }));
                      }
                    }}
                    selectOptions={discountUnitOptions}
                    selectValue={newStock.discount.unit}
                    onSelectChange={(val) => {
                      const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ discountUnit: val });
                      setStock((prev) => ({
                        ...prev,
                        discount: { ...prev.discount, unit: val || 'Rs' },
                        unitCost: calculatedUnitPrice,
                        costOfGoods: calculatedTotalCost,
                      }));
                    }}
                    selectPlaceholder="Unit"
                    error={errors.discount}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <Input
                    label="Cost of Goods"
                    type="text"
                    value={newStock.costOfGoods}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setErrors((e) => ({ ...e, costOfGoods: '' }));
                      if (v === '') {
                        setUnitCostDisabled(false);
                        setStock({ unitCost: '', costOfGoods: '' });
                        return;
                      }
                      setStock({ costOfGoods: v });
                      const num = parseFloat(v);
                      if (!Number.isNaN(num)) {
                        setUnitCostDisabled(true);
                        const { calculatedUnitPrice } = onchangeBuyingValue({
                          costOfGoods: num,
                          isCostOfGoodsEntered: true,
                          isUnitCostEntered: false,
                        });
                        setStock((prev) => ({ ...prev, unitCost: calculatedUnitPrice }));
                      }
                    }}
                    disabled={isCostOfGoodsDisabled}
                    error={errors.costOfGoods}
                  />
                </div>
              </div>
            </section>

            <div className={styles.divider} />

            <section className={styles.formSection}>
              <h2 className={styles.sectionTitle}>Selling Section</h2>
              <p className={styles.sectionCaption}>Please add your selling information</p>
              <div className={styles.fieldGrid}>
                <div className={styles.fieldGroup}>
                  <Dropdown
                    label="Profit Margin Type"
                    options={PROFIT_MARGIN_TYPE_OPTIONS}
                    value={profitMargingType}
                    onChange={(val) => {
                      setProfitMargingType(val);
                      const calc = calculateSellingPrice({ profitMargingType: val });
                      setStock((prev) => ({ ...prev, sellingPrice: calc }));
                    }}
                    placeholder="Select"
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <InputWithSelect
                    label={profitMargingType === 'selling' ? 'Profit Margin (per unit)' : 'Profit Markup (per unit)'}
                    value={profitMargin}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setProfitMargin(v);
                      const num = parseFloat(v);
                      if (!Number.isNaN(num)) {
                        const calc = calculateSellingPrice({ profitMargin: num });
                        setStock((prev) => ({ ...prev, sellingPrice: calc }));
                      }
                    }}
                    selectOptions={discountUnitOptions}
                    selectValue={profitMarginUnit}
                    onSelectChange={(val) => {
                      setProfitMarginUnit(val || '%');
                      const num = parseFloat(profitMargin);
                      if (!Number.isNaN(num)) {
                        const calc = calculateSellingPrice({ profitMargin: num, profitMarginUnit: val });
                        setStock((prev) => ({ ...prev, sellingPrice: calc }));
                      }
                    }}
                    selectPlaceholder="Unit"
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <Input
                    label="Selling Price"
                    type="text"
                    value={newStock.sellingPrice}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setErrors((e) => ({ ...e, sellingPrice: '' }));
                      setStock({ sellingPrice: v });
                      const num = parseFloat(v);
                      if (!Number.isNaN(num)) {
                        const calc = calculateProfitMargin({ sellingPrice: num });
                        setProfitMargin(calc);
                      }
                    }}
                    error={errors.sellingPrice}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <InputWithSelect
                    label="Selling Discount"
                    value={newStock.sellingDiscount.value}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setStock((prev) => ({ ...prev, sellingDiscount: { ...prev.sellingDiscount, value: v } }));
                    }}
                    selectOptions={discountUnitOptions}
                    selectValue={newStock.sellingDiscount.unit}
                    onSelectChange={(val) =>
                      setStock((prev) => ({
                        ...prev,
                        sellingDiscount: { ...prev.sellingDiscount, unit: val || 'Rs' },
                      }))
                    }
                    selectPlaceholder="Unit"
                  />
                </div>
              </div>
              <div className={styles.finalPriceRow}>
                <span className={styles.finalPriceLabel}>Final Selling Price</span>
                <span className={styles.finalPriceValue}>Rs {finalSellingPrice}</span>
              </div>
              <div className={styles.fieldGrid}>
                <div className={styles.fieldGroup}>
                  <InputWithSelect
                    label="Loyalty Discount"
                    value={newStock.sellingLoyaltyDiscount.value}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setStock((prev) => ({ ...prev, sellingLoyaltyDiscount: { ...prev.sellingLoyaltyDiscount, value: v } }));
                    }}
                    selectOptions={discountUnitOptions}
                    selectValue={newStock.sellingLoyaltyDiscount.unit}
                    onSelectChange={(val) =>
                      setStock((prev) => ({
                        ...prev,
                        sellingLoyaltyDiscount: { ...prev.sellingLoyaltyDiscount, unit: val || 'Rs' },
                      }))
                    }
                    selectPlaceholder="Unit"
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <InputWithSelect
                    label="Wholesale Discount"
                    value={newStock.sellingWholesaleDiscount.value}
                    onChange={(v) => {
                      if (v !== '' && !NUMERIC_REGEX.test(v)) return;
                      setStock((prev) => ({ ...prev, sellingWholesaleDiscount: { ...prev.sellingWholesaleDiscount, value: v } }));
                    }}
                    selectOptions={discountUnitOptions}
                    selectValue={newStock.sellingWholesaleDiscount.unit}
                    onSelectChange={(val) =>
                      setStock((prev) => ({
                        ...prev,
                        sellingWholesaleDiscount: { ...prev.sellingWholesaleDiscount, unit: val || 'Rs' },
                      }))
                    }
                    selectPlaceholder="Unit"
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <Input
                    label="Expire Date"
                    type="date"
                    value={expDate}
                    onChange={(v) => setExpDate(v || '')}
                  />
                </div>
              </div>
            </section>

            <div className={styles.divider} />

            <section className={styles.formSection}>
              <div className={styles.sectionTitleContainer}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>Serial Numbers</div>
                  <div className={styles.sectionCaption}>Add serial numbers for this stock item</div>
                </div>
              </div>

              <div className={styles.serialEntryRow}>
                <div className={styles.serialEntryCol}>
                  <Input
                    label="Serial Number"
                    value={warrantyDetails.serialNumber}
                    onChange={(v) => setWarrantyDetails((prev) => ({ ...prev, serialNumber: v || '' }))}
                    placeholder="Enter serial number"
                    disabled={isSingleSerialDisabled}
                  />
                  <div className={styles.inlineActions}>
                    <Button type="button" variant="secondary" onClick={addSerialNumber} disabled={isSingleSerialDisabled}>
                      Add
                    </Button>
                  </div>
                </div>

                <div className={styles.serialRangeBlock}>
                  <div className={styles.serialRangeTitle}>Or serial range</div>
                  <div className={styles.serialRangeRow}>
                    <Input
                      label="Min"
                      value={warrantyDetails.serialRange.min}
                      onChange={(v) =>
                        setWarrantyDetails((prev) => ({
                          ...prev,
                          serialRange: { ...prev.serialRange, min: v || '' },
                        }))
                      }
                      placeholder="Min"
                      disabled={isSerialRangeDisabled}
                    />
                    <div className={styles.serialRangeDash}>-</div>
                    <Input
                      label="Max"
                      value={warrantyDetails.serialRange.max}
                      onChange={(v) =>
                        setWarrantyDetails((prev) => ({
                          ...prev,
                          serialRange: { ...prev.serialRange, max: v || '' },
                        }))
                      }
                      placeholder="Max"
                      disabled={isSerialRangeDisabled}
                    />
                    <div className={styles.inlineActions}>
                      <Button type="button" variant="secondary" onClick={handleGenerateSerialRange} disabled={isSerialRangeDisabled}>
                        Generate
                      </Button>
                    </div>
                  </div>
                  {isSerialRangeDisabled && (
                    <div className={styles.helperText}>
                      Serial range is disabled because serial numbers are already added.
                    </div>
                  )}
                </div>
              </div>

              {isSingleSerialDisabled && (
                <div className={styles.helperText}>
                  Single serial input is disabled because a serial range is entered.
                </div>
              )}

              <div className={styles.tagsWrap}>
                {(warrantyDetails.serialNumberList || []).map((s, index) => (
                  <span
                    key={(typeof s === 'object' && s?.id) ? s.id : `sn-${index}-${getSerialValue(s)}`}
                    className={styles.tag}
                  >
                    <span className={styles.tagText}>{getSerialValue(s)}</span>
                    <button
                      type="button"
                      className={styles.tagRemove}
                      onClick={() => removeSerialAt(index)}
                      aria-label="Remove serial"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {(warrantyDetails.serialNumberList || []).length > 0 && (
                  <button type="button" className={styles.clearAll} onClick={clearAllSerials}>
                    Clear all
                  </button>
                )}
              </div>
            </section>

            <div className={styles.divider} />

            <section className={styles.formSection}>
              <div className={styles.sectionTitleContainer}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>Warranty Information</div>
                  <div className={styles.sectionCaption}>Attach warranty details (optional)</div>
                </div>
              </div>

              <div className={styles.warrantyRow}>
                <Dropdown
                  label="Select Warranty"
                  value={warrantyDetails.warrantyType?.id ?? ''}
                  onChange={(val) => {
                    const selected = warrantyTypeOptions.find((w) => String(w.id) === String(val));
                    setWarrantyDetails((prev) => ({ ...prev, warrantyType: selected || null }));
                    if (selected?.period && selected?.unit) {
                      setDisplayWarrantyPeriod(`${selected.period} ${selected.unit}`);
                    } else {
                      setDisplayWarrantyPeriod('');
                    }
                  }}
                  options={uiWarrantyTypeOptions}
                  placeholder="Not Selected"
                />

                <Input
                  label="Description"
                  value={warrantyDetails.warrantyDescription}
                  onChange={(v) => setWarrantyDetails((prev) => ({ ...prev, warrantyDescription: v || '' }))}
                  placeholder="Optional"
                />

                <div className={styles.inlineActions}>
                  <Button type="button" variant="secondary" onClick={addWarranty}>
                    Add
                  </Button>
                </div>
              </div>

              {displayWarrantyPeriod && (
                <div className={styles.helperText}>Period: {displayWarrantyPeriod}</div>
              )}

              <div className={styles.tagsWrap}>
                {(warrantyDetails.warranty || []).map((w, index) => (
                  <span key={`${w.warrantyType?.id ?? index}-${index}`} className={styles.tag}>
                    <span className={styles.tagText}>
                      {w.warrantyType?.name ?? String(w.warrantyType?.id ?? '')}
                      {w.description ? ` — ${w.description}` : ''}
                    </span>
                    <button
                      type="button"
                      className={styles.tagRemove}
                      onClick={() => removeWarrantyAt(index)}
                      aria-label="Remove warranty"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {(warrantyDetails.warranty || []).length > 0 && (
                  <button type="button" className={styles.clearAll} onClick={clearAllWarranties}>
                    Clear all
                  </button>
                )}
              </div>
            </section>

            <div className={styles.buttonPanel}>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Submit'}
              </Button>
              <Button type="button" variant="secondary" onClick={handleReset} disabled={isSubmitting}>
                Reset
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  stocksState: state.stocksState,
  auth: state.authState,
  suppliersState: state.suppliersState,
});

export default connect(mapStateToProps, {
  getSuppliers,
  searchProductsForAddStock,
  getProductByIdForAddStock,
  createStock,
  showAlert,
})(AddNewStock);
